// Pod Rotation schedule generator — pure functions, ported/adapted from
// pod_rotation_reference.py. ALL constraints are GM-supplied data; nothing about
// specific people is baked in. Runs client-side in the GM wizard; the resulting
// schedule is locked and stored on the game.
//
// Constraint semantics:
//   keepTogether [a,b]: a & b are ALWAYS in the same pod AND same squad (chained
//     via union-find; a group can be >2 people). One indivisible unit.
//   keepApart   [a,b]: a & b are NEVER in the same pod or squad, any round.
//   lateArrivals {p: roundIdx}: p's pod hides in round >= roundIdx; p is excluded
//     from seeker squads until then.
//   swing: casual free-floaters. They pad core pods to 4 (don't create new pods),
//     can't be in keepTogether (so the GM may move them anytime), and the GM can
//     reassign them at runtime.

export type Pair = [string, string];
export interface ScheduleInput {
  players: string[];
  keepTogether?: Pair[];
  keepApart?: Pair[];
  lateArrivals?: Record<string, number>;
  swing?: string[];
  maxSquad?: number;
  seed?: number;
}
export interface PodSchedule {
  pods: string[][];          // pods[i] = members of the pod that hides in round i
  rounds: string[][][];      // rounds[i] = list of seeker squads for round i
}

export class ConstraintError extends Error {
  constructor(message: string) { super(message); this.name = "ConstraintError"; }
}

// Small seeded RNG (mulberry32) so generation is reproducible given a seed; the
// wizard passes a fresh random seed each "Regenerate".
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffle<T>(arr: T[], rand: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}
function pairKey(a: string, b: string): string { return a < b ? `${a}|${b}` : `${b}|${a}`; }

// Union-find merge of keepTogether pairs → list of groups.
function mergeTogether(players: string[], keepTogether: Pair[]): string[][] {
  const parent: Record<string, string> = {};
  players.forEach((p) => (parent[p] = p));
  const find = (x: string): string => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const union = (a: string, b: string) => { parent[find(a)] = find(b); };
  for (const [a, b] of keepTogether) if (a in parent && b in parent) union(a, b);
  const groups: Record<string, string[]> = {};
  for (const p of players) (groups[find(p)] ??= []).push(p);
  return Object.values(groups);
}

function violatesPod(pod: string[], keepTogether: Pair[], keepApart: Pair[]): boolean {
  const s = new Set(pod);
  for (const [a, b] of keepApart) if (s.has(a) && s.has(b)) return true;
  for (const [a, b] of keepTogether) if (s.has(a) !== s.has(b)) return true;
  return false;
}

// Order pods so any pod with a late arrival hides in a round >= its arrival.
function orderRounds(pods: string[][], lateArrivals: Record<string, number>): string[][] | null {
  const req = pods.map((pod) => Math.max(0, ...pod.map((p) => lateArrivals[p] ?? 0)));
  const order = pods.map((pod, i) => ({ pod, r: req[i] })).sort((x, y) => x.r - y.r).map((x) => x.pod);
  for (let i = 0; i < order.length; i++) {
    if (Math.max(0, ...order[i].map((p) => lateArrivals[p] ?? 0)) > i) return null;
  }
  return order;
}

// Build pods over CORE players (swing excluded from the count), then pad with
// swing (preferring smaller pods), respecting keepApart. Returns round-ordered pods.
export function generatePods(input: ScheduleInput): string[][] {
  const keepTogether = input.keepTogether ?? [];
  const keepApart = input.keepApart ?? [];
  const lateArrivals = input.lateArrivals ?? {};
  const swing = new Set(input.swing ?? []);
  const core = input.players.filter((p) => !swing.has(p));
  if (input.players.length === 0) throw new ConstraintError("Add some players first.");

  // Swing players can't be glued together (they must stay freely movable).
  for (const [a, b] of keepTogether) if (swing.has(a) || swing.has(b)) {
    throw new ConstraintError(`A swing player is in an "always together" rule (${a} & ${b}). Swing players must stay movable — unflag swing or drop that rule.`);
  }

  // Prefer pods of 3, absorbing the remainder into pods of 4 — never 2s. Using the
  // FEWEST pods (floor(core/3)) means the remainder (core % 3) bumps that many pods up
  // to 4 instead of trimming pods down to 2. Only fall back to other sizes when 3/4
  // can't tile core (core < 3, or core === 5 → [3,2], which is unavoidable).
  let numPods = Math.max(1, Math.floor(core.length / 3));
  while (numPods * 4 < core.length) numPods += 1; // too many for ≤4 each → add a pod
  const sizes = new Array(numPods).fill(3);
  let i = 0;
  while (sizes.reduce((s, v) => s + v, 0) < core.length) { sizes[i % numPods] += 1; i++; }
  while (sizes.reduce((s, v) => s + v, 0) > core.length) { sizes[i % numPods] -= 1; i++; }

  const groups = mergeTogether(core, keepTogether);
  if (groups.length && Math.max(...groups.map((g) => g.length)) > Math.max(...sizes)) {
    throw new ConstraintError(`An "always together" group has ${Math.max(...groups.map((g) => g.length))} players, but pods only hold up to ${Math.max(...sizes)}. Split that group or remove a member.`);
  }

  const rand = rng(input.seed ?? 7);
  const ATTEMPTS = 200000;
  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    const units = shuffle(groups, rand);
    const flat = units.flat();
    const pods: string[][] = [];
    let idx = 0;
    for (const s of sizes) { pods.push(flat.slice(idx, idx + s)); idx += s; }
    if (pods.some((p) => violatesPod(p, keepTogether, keepApart))) continue;

    // Pad with swing players (smallest pod first; respect keepApart).
    const padded = pods.map((p) => p.slice());
    let placedAll = true;
    for (const sp of shuffle(input.swing ?? [], rand)) {
      const candidates = padded.map((p, pi) => ({ pi, len: p.length }))
        .sort((x, y) => x.len - y.len)
        .filter(({ pi }) => !keepApart.some(([a, b]) => (a === sp && padded[pi].includes(b)) || (b === sp && padded[pi].includes(a))));
      if (!candidates.length) { placedAll = false; break; }
      padded[candidates[0].pi].push(sp);
    }
    if (!placedAll) continue;

    const ordered = orderRounds(padded, lateArrivals);
    if (ordered) return ordered;
  }
  throw new ConstraintError("Couldn't satisfy the pod constraints. Relax a “never together” or “always together” rule, or adjust who's a swing player.");
}

// Per round, partition arrived non-hiding players into squads (<= maxSquad),
// honoring constraints, minimizing repeat partnerships across rounds.
export function generateSquads(pods: string[][], input: ScheduleInput): string[][][] {
  const keepTogether = input.keepTogether ?? [];
  const keepApart = input.keepApart ?? [];
  const lateArrivals = input.lateArrivals ?? {};
  const maxSquad = input.maxSquad ?? 3;
  const all = pods.flat();
  const pairCount: Record<string, number> = {};

  const squadOk = (s: string[]): boolean => {
    const set = new Set(s);
    for (const [a, b] of keepApart) if (set.has(a) && set.has(b)) return false;
    for (const [a, b] of keepTogether) if (set.has(a) !== set.has(b)) return false;
    return true;
  };
  const cost = (squads: string[][]): number => {
    let c = 0;
    for (const s of squads) for (let i = 0; i < s.length; i++) for (let j = i + 1; j < s.length; j++) c += pairCount[pairKey(s[i], s[j])] ?? 0;
    return c;
  };

  const groups = mergeTogether(all, keepTogether);
  const groupOf: Record<string, string[]> = {};
  for (const g of groups) { const key = [...g].sort(); for (const p of g) groupOf[p] = key; }

  const schedule: string[][][] = [];
  pods.forEach((hidingPod, roundIdx) => {
    const hiders = new Set(hidingPod);
    const seekers = all.filter((p) => !hiders.has(p) && (lateArrivals[p] ?? 0) <= roundIdx);
    // Glue together-groups into units.
    const seen = new Set<string>(); const units: string[][] = [];
    for (const p of seekers) {
      const g = groupOf[p]; const gk = g.join("|");
      if (seen.has(gk)) continue; seen.add(gk);
      units.push(g.filter((x) => seekers.includes(x)));
    }
    // No seekers this round (e.g. a single-pod game where everyone hides) → no squads.
    if (units.length === 0) { schedule.push([]); return; }
    const rand = rng((input.seed ?? 1000) + roundIdx + 1);
    let best: string[][] | null = null, bestCost = Infinity;
    // Partition units into BALANCED squads: use the fewest squads that fit
    // (ceil(seekers / maxSquad)), and place each unit into the smallest squad that can
    // still hold it. Balancing keeps sizes even (e.g. [3,3,2,2]) and never leaves a
    // leftover 1-person "squad". If constraints make the minimum count infeasible, try
    // one more squad, and so on.
    const minSquads = Math.max(1, Math.ceil(seekers.length / maxSquad));
    for (let nSquads = minSquads; nSquads <= Math.max(minSquads, units.length) && !best; nSquads++) {
      for (let attempt = 0; attempt < 60000; attempt++) {
        const shuffled = shuffle(units, rand);
        const squads: string[][] = Array.from({ length: nSquads }, () => [] as string[]);
        let placedAll = true;
        for (const u of shuffled) {
          const target = squads
            .map((s, idx) => ({ idx, size: s.length }))
            // Fits under maxSquad, OR the squad is empty (lets an indivisible
            // "always together" group larger than maxSquad occupy its own squad).
            .filter(({ idx }) => (squads[idx].length === 0 || squads[idx].length + u.length <= maxSquad) && squadOk([...squads[idx], ...u]))
            .sort((a, b) => a.size - b.size)[0];
          if (!target) { placedAll = false; break; }
          squads[target.idx].push(...u);
        }
        if (!placedAll || squads.some((s) => s.length === 0)) continue;
        const c = cost(squads);
        if (c < bestCost) { best = squads.map((s) => s.slice()); bestCost = c; }
        if (c === 0) break;
      }
    }
    if (!best) throw new ConstraintError(`Round ${roundIdx + 1}: can't form valid seeker squads under the current constraints. Relax a rule or raise the max squad size.`);
    for (const s of best) for (let i = 0; i < s.length; i++) for (let j = i + 1; j < s.length; j++) { const k = pairKey(s[i], s[j]); pairCount[k] = (pairCount[k] ?? 0) + 1; }
    schedule.push(best);
  });
  return schedule;
}

export function buildSchedule(input: ScheduleInput): PodSchedule {
  const pods = generatePods(input);
  const rounds = generateSquads(pods, input);
  return { pods, rounds };
}

// Validate a (possibly hand-edited) set of pods against the pairing rules.
// Returns null if OK, else an explanatory message (with player ids).
export function validatePods(pods: string[][], keepTogether: Pair[] = [], keepApart: Pair[] = []): string | null {
  const podOf = (x: string) => pods.findIndex((p) => p.includes(x));
  for (const [a, b] of keepApart) { const pa = podOf(a); if (pa >= 0 && pa === podOf(b)) return `${a} and ${b} are never-together but are in the same pod.`; }
  for (const [a, b] of keepTogether) { const pa = podOf(a), pb = podOf(b); if (pa >= 0 && pb >= 0 && pa !== pb) return `${a} and ${b} must stay together but are in different pods.`; }
  return null;
}
